import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { startOfDay, startOfWeek, startOfMonth, subDays, format } from "date-fns";
import type { TimeRange } from "@/components/dashboard/DashboardTimeFilter";

interface Trade {
  id: string;
  date: string;
  time: string;
  instrument: string;
  direction: string;
  pnl: number;
  pnl_percent: number;
  rr: number;
  session: string;
  strategy: string;
  emotion: string;
  created_at: string;
  prop_account_id: string | null;
}

interface PropAccount {
  id: string;
  account_size: number;
  created_at: string;
}

interface BrokerPosition {
  unrealized_pnl: number;
}

function getTradeTimestamp(trade: Trade): Date {
  const rawTime = trade.time?.trim();
  const normalizedTime = rawTime
    ? rawTime.length === 5
      ? `${rawTime}:00`
      : rawTime
    : trade.created_at.slice(11, 19);
  const parsed = new Date(`${trade.date}T${normalizedTime}`);
  return Number.isNaN(parsed.getTime()) ? new Date(trade.created_at) : parsed;
}

function filterByTimeRange(trades: Trade[], range: TimeRange): Trade[] {
  const now = new Date();
  let cutoff: Date;
  switch (range) {
    case "today":
      cutoff = startOfDay(now);
      break;
    case "week":
      cutoff = startOfWeek(now, { weekStartsOn: 1 });
      break;
    case "month":
      cutoff = startOfMonth(now);
      break;
    case "all":
    default:
      return trades;
  }
  const cutoffStr = format(cutoff, "yyyy-MM-dd");
  return trades.filter((t) => t.date >= cutoffStr);
}

function computeStreak(trades: Trade[]): { count: number; type: "win" | "loss" } {
  if (!trades.length) return { count: 0, type: "win" };
  const sorted = [...trades].sort((a, b) => b.date.localeCompare(a.date) || b.created_at.localeCompare(a.created_at));
  const firstType = sorted[0].pnl >= 0 ? "win" : "loss";
  let count = 0;
  for (const t of sorted) {
    if ((t.pnl >= 0 ? "win" : "loss") === firstType) count++;
    else break;
  }
  return { count, type: firstType };
}

function computeEquityCurve(trades: Trade[], startingBalance: number, currentUnrealizedPnl: number) {
  if (!trades.length) return [];

  const sorted = [...trades].sort((a, b) => getTradeTimestamp(a).getTime() - getTradeTimestamp(b).getTime());
  const firstTimestamp = getTradeTimestamp(sorted[0]).getTime();
  const lastTimestamp = getTradeTimestamp(sorted[sorted.length - 1]).getTime();
  const showTime = lastTimestamp - firstTimestamp <= 1000 * 60 * 60 * 24 * 7;
  let balance = startingBalance;
  let peak = balance;
  const data: { date: string; label: string; balance: number; equity: number; drawdown: number }[] = [];

  sorted.forEach((trade, index) => {
    balance += Number(trade.pnl);
    const timestamp = getTradeTimestamp(trade);
    const equity = balance + (index === sorted.length - 1 ? currentUnrealizedPnl : 0);
    peak = Math.max(peak, balance, equity);
    const dd = peak > 0 ? ((peak - Math.min(balance, equity)) / peak) * 100 : 0;

    data.push({
      date: timestamp.toISOString(),
      label: format(timestamp, showTime ? "MMM d HH:mm" : "MMM d"),
      balance: Math.round(balance * 100) / 100,
      equity: Math.round(equity * 100) / 100,
      drawdown: Math.round(dd * 100) / 100,
    });

  });

  return data;
}

function computeSessionData(trades: Trade[]) {
  const sessions = new Map<string, { pnl: number; trades: number; wins: number }>();
  for (const t of trades) {
    const s = t.session || "Other";
    const curr = sessions.get(s) || { pnl: 0, trades: 0, wins: 0 };
    curr.pnl += Number(t.pnl);
    curr.trades += 1;
    if (Number(t.pnl) > 0) curr.wins += 1;
    sessions.set(s, curr);
  }
  return Array.from(sessions.entries()).map(([session, d]) => ({
    session,
    pnl: Math.round(d.pnl),
    trades: d.trades,
    winRate: d.trades > 0 ? Math.round((d.wins / d.trades) * 100) : 0,
  }));
}

function computePairData(trades: Trade[]) {
  const pairs = new Map<string, { trades: number; wins: number; totalRR: number; pnl: number }>();
  for (const t of trades) {
    const curr = pairs.get(t.instrument) || { trades: 0, wins: 0, totalRR: 0, pnl: 0 };
    curr.trades += 1;
    if (Number(t.pnl) > 0) curr.wins += 1;
    curr.totalRR += Number(t.rr);
    curr.pnl += Number(t.pnl);
    pairs.set(t.instrument, curr);
  }
  return Array.from(pairs.entries()).map(([pair, d]) => ({
    pair,
    trades: d.trades,
    winRate: d.trades > 0 ? Math.round((d.wins / d.trades) * 100) : 0,
    avgRR: d.trades > 0 ? Math.round((d.totalRR / d.trades) * 100) / 100 : 0,
    pnl: Math.round(d.pnl),
  }));
}

function computeSparkline(trades: Trade[], metric: "pnl" | "winRate" | "rr"): number[] {
  // Last 7 days of data points
  const now = new Date();
  const points: number[] = [];
  for (let i = 6; i >= 0; i--) {
    const day = format(subDays(now, i), "yyyy-MM-dd");
    const dayTrades = trades.filter((t) => t.date === day);
    if (metric === "pnl") {
      points.push(dayTrades.reduce((s, t) => s + Number(t.pnl), 0));
    } else if (metric === "winRate") {
      const wins = dayTrades.filter((t) => Number(t.pnl) > 0).length;
      points.push(dayTrades.length > 0 ? (wins / dayTrades.length) * 100 : 0);
    } else {
      const avg = dayTrades.length > 0
        ? dayTrades.reduce((s, t) => s + Number(t.rr), 0) / dayTrades.length
        : 0;
      points.push(avg);
    }
  }
  return points;
}

function gradeValue(value: number, thresholds: { excellent: number; good: number; average: number }): "excellent" | "good" | "average" | "poor" {
  if (value >= thresholds.excellent) return "excellent";
  if (value >= thresholds.good) return "good";
  if (value >= thresholds.average) return "average";
  return "poor";
}

export function useDashboardData(timeRange: TimeRange, excludeEval = true) {
  const { user } = useAuth();

  const { data: allTrades = [], isLoading, refetch: refetchTrades } = useQuery({
    queryKey: ["dashboard-trades", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("trades")
        .select("id, date, time, instrument, direction, pnl, pnl_percent, rr, session, strategy, emotion, created_at, prop_account_id")
        .eq("user_id", user.id)
        .order("date", { ascending: true });
      if (error) throw error;
      return (data || []) as Trade[];
    },
    enabled: !!user?.id,
  });

  const { data: propAccounts = [], refetch: refetchPropAccounts } = useQuery({
    queryKey: ["dashboard-prop-accounts", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("prop_accounts")
        .select("id, account_size, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as PropAccount[];
    },
    enabled: !!user?.id,
  });

  const { data: openPositions = [], refetch: refetchOpenPositions } = useQuery({
    queryKey: ["dashboard-open-positions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("broker_positions")
        .select("unrealized_pnl")
        .eq("user_id", user.id);
      if (error) throw error;
      return (data || []) as BrokerPosition[];
    },
    enabled: !!user?.id,
  });

  const { data: violations = [], refetch: refetchViolations } = useQuery({
    queryKey: ["dashboard-violations", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("violations")
        .select("id, date, type, severity")
        .eq("user_id", user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const evalTradeCount = allTrades.filter((t) => !!t.prop_account_id).length;
  const effectiveTrades = excludeEval ? allTrades.filter((t) => !t.prop_account_id) : allTrades;

  const trades = filterByTimeRange(effectiveTrades, timeRange);
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todayTrades = effectiveTrades.filter((t) => t.date === todayStr);
  const todayViolations = violations.filter((v) => v.date === todayStr);

  // Last week's data for deltas
  const lastWeekStart = format(subDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 7), "yyyy-MM-dd");
  const lastWeekEnd = format(subDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 1), "yyyy-MM-dd");
  const lastWeekTrades = effectiveTrades.filter((t) => t.date >= lastWeekStart && t.date <= lastWeekEnd);
  const thisWeekTrades = filterByTimeRange(effectiveTrades, "week");

  // KPIs
  const totalPnl = trades.reduce((s, t) => s + Number(t.pnl), 0);
  const wins = trades.filter((t) => Number(t.pnl) > 0).length;
  const winRate = trades.length > 0 ? Math.round((wins / trades.length) * 100) : 0;
  const avgRR = trades.length > 0
    ? Math.round((trades.reduce((s, t) => s + Number(t.rr), 0) / trades.length) * 100) / 100
    : 0;
  const totalWins = trades.filter((t) => Number(t.pnl) > 0).reduce((s, t) => s + Number(t.pnl), 0);
  const totalLosses = Math.abs(trades.filter((t) => Number(t.pnl) < 0).reduce((s, t) => s + Number(t.pnl), 0));
  const profitFactor = totalLosses > 0 ? Math.round((totalWins / totalLosses) * 100) / 100 : totalWins > 0 ? Infinity : 0;
  const streak = computeStreak(trades);

  // Weekly deltas
  const lwWinRate = lastWeekTrades.length > 0 ? (lastWeekTrades.filter((t) => Number(t.pnl) > 0).length / lastWeekTrades.length) * 100 : 0;
  const twWinRate = thisWeekTrades.length > 0 ? (thisWeekTrades.filter((t) => Number(t.pnl) > 0).length / thisWeekTrades.length) * 100 : 0;
  const winRateDelta = Math.round((twWinRate - lwWinRate) * 10) / 10;

  const lwAvgRR = lastWeekTrades.length > 0 ? lastWeekTrades.reduce((s, t) => s + Number(t.rr), 0) / lastWeekTrades.length : 0;
  const twAvgRR = thisWeekTrades.length > 0 ? thisWeekTrades.reduce((s, t) => s + Number(t.rr), 0) / thisWeekTrades.length : 0;
  const rrDelta = lwAvgRR > 0 ? Math.round(((twAvgRR - lwAvgRR) / lwAvgRR) * 100 * 10) / 10 : 0;

  // Sparklines from effective trades (7-day window)
  const pnlSparkline = computeSparkline(effectiveTrades, "pnl");
  const wrSparkline = computeSparkline(effectiveTrades, "winRate");
  const rrSparkline = computeSparkline(effectiveTrades, "rr");

  const accountSizeById = new Map(propAccounts.map((account) => [account.id, Number(account.account_size)]));
  const referencedAccountIds = Array.from(new Set(effectiveTrades.map((trade) => trade.prop_account_id).filter(Boolean))) as string[];
  const baseStartingBalance = referencedAccountIds.length > 0
    ? referencedAccountIds.reduce((sum, id) => sum + (accountSizeById.get(id) ?? 0), 0)
    : Number(propAccounts[0]?.account_size ?? 0);
  const visibleTradeIds = new Set(trades.map((trade) => trade.id));
  const openingBalance = baseStartingBalance + effectiveTrades
    .filter((trade) => !visibleTradeIds.has(trade.id))
    .reduce((sum, trade) => sum + Number(trade.pnl), 0);
  const currentUnrealizedPnl = openPositions.reduce((sum, position) => sum + Number(position.unrealized_pnl), 0);

  // Equity curve
  const startingBalance = Math.round(openingBalance * 100) / 100;
  const equityCurve = computeEquityCurve(trades, startingBalance, currentUnrealizedPnl);

  // Session & pair data
  const sessionData = computeSessionData(trades);
  const pairData = computePairData(trades);

  // Risk metrics
  const avgRiskPerTrade = trades.length > 0
    ? Math.round(Math.abs(trades.reduce((s, t) => s + Number(t.pnl_percent), 0) / trades.length) * 100) / 100
    : 0;
  const maxDD = equityCurve.length > 0 ? Math.max(...equityCurve.map((d) => d.drawdown)) : 0;

  // Feed items from recent trades
  const recentTrades = [...effectiveTrades]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 8);
  const feedItems = recentTrades.map((t) => ({
    id: t.id,
    type: (Number(t.pnl) > 0 ? "win" : Number(t.pnl) < 0 ? "loss" : "info") as "win" | "loss" | "info",
    message: `${t.instrument} ${Number(t.pnl) >= 0 ? "+" : ""}${Number(t.rr).toFixed(1)}R ${Number(t.pnl) >= 0 ? "closed" : "SL hit"} (${t.session || "N/A"})`,
    time: format(new Date(t.created_at), "MMM d"),
  }));

  // AI insights (data-driven)
  const aiInsights = generateInsights(trades, sessionData, pairData);

  return {
    isLoading,
    refetch: async () => { await Promise.all([refetchTrades(), refetchViolations(), refetchPropAccounts(), refetchOpenPositions()]); },
    hasTrades: effectiveTrades.length > 0,
    evalTradeCount,
    todayPnl: Math.round(todayTrades.reduce((s, t) => s + Number(t.pnl), 0)),
    totalPnl: Math.round(totalPnl),
    winRate,
    avgRR,
    profitFactor: profitFactor === Infinity ? 99.9 : profitFactor,
    streak,
    tradeCount: trades.length,
    todayTradeCount: todayTrades.length,
    violationsToday: todayViolations.length,
    pnlSparkline,
    wrSparkline,
    rrSparkline,
    winRateDelta,
    rrDelta,
    equityCurve,
    startingBalance,
    sessionData,
    pairData,
    feedItems,
    aiInsights,
    avgRiskPerTrade,
    maxDD,
    grades: {
      pnl: gradeValue(totalPnl, { excellent: 1000, good: 0, average: -500 }),
      winRate: gradeValue(winRate, { excellent: 65, good: 50, average: 40 }),
      rr: gradeValue(avgRR, { excellent: 2, good: 1.5, average: 1 }),
      profitFactor: gradeValue(profitFactor === Infinity ? 99.9 : profitFactor, { excellent: 2.5, good: 1.5, average: 1 }),
    },
  };
}

function generateInsights(
  trades: Trade[],
  sessionData: ReturnType<typeof computeSessionData>,
  pairData: ReturnType<typeof computePairData>
) {
  const insights: { id: string; text: string; type: "positive" | "negative" | "neutral" }[] = [];

  if (trades.length < 3) {
    insights.push({ id: "no-data", text: "Log more trades to unlock data-driven insights. At least 3 trades needed.", type: "neutral" });
    return insights;
  }

  // Direction bias
  const longs = trades.filter((t) => t.direction === "long");
  const shorts = trades.filter((t) => t.direction === "short");
  const longWR = longs.length > 0 ? (longs.filter((t) => Number(t.pnl) > 0).length / longs.length) * 100 : 0;
  const shortWR = shorts.length > 0 ? (shorts.filter((t) => Number(t.pnl) > 0).length / shorts.length) * 100 : 0;
  if (Math.abs(longWR - shortWR) > 15 && longs.length >= 2 && shorts.length >= 2) {
    const better = longWR > shortWR ? "long" : "short";
    const diff = Math.round(Math.abs(longWR - shortWR));
    insights.push({
      id: "direction",
      text: `Win rate is ${diff}% higher on ${better} positions. Consider leaning into ${better === "long" ? "bullish" : "bearish"} setups.`,
      type: "positive",
    });
  }

  // Best session
  const bestSession = sessionData.sort((a, b) => b.pnl - a.pnl)[0];
  const worstSession = sessionData.sort((a, b) => a.pnl - b.pnl)[0];
  if (bestSession && worstSession && bestSession.session !== worstSession.session && sessionData.length > 1) {
    insights.push({
      id: "session",
      text: `${bestSession.session} is your best session (+$${bestSession.pnl.toLocaleString()}). ${worstSession.pnl < 0 ? `Avoid ${worstSession.session} where you're down $${Math.abs(worstSession.pnl).toLocaleString()}.` : ""}`,
      type: worstSession.pnl < 0 ? "negative" : "positive",
    });
  }

  // Best pair
  const bestPair = [...pairData].sort((a, b) => b.winRate - a.winRate)[0];
  if (bestPair && bestPair.trades >= 3) {
    insights.push({
      id: "pair",
      text: `${bestPair.pair} is your best-performing pair with ${bestPair.winRate}% win rate over ${bestPair.trades} trades.`,
      type: "positive",
    });
  }

  // Overtraded pair
  const overtradedPair = [...pairData].sort((a, b) => b.trades - a.trades)[0];
  if (overtradedPair && overtradedPair.winRate < 45 && overtradedPair.trades >= 5) {
    insights.push({
      id: "overtrade",
      text: `${overtradedPair.pair} has ${overtradedPair.trades} trades but only ${overtradedPair.winRate}% win rate. Consider reducing exposure.`,
      type: "negative",
    });
  }

  if (insights.length === 0) {
    insights.push({ id: "neutral", text: "Keep logging trades consistently to unlock deeper performance patterns.", type: "neutral" });
  }

  return insights;
}
